<template>
  <RevDrawer
    :name="MODAL_NAMES.CREATE"
    :title="i18n(translations.title)"
    @close="handleClose"
  >
    <template #body>
      <template v-if="currentStep === FormStep.ChooseIntegrator">
        <p class="body-1-bold mb-32">
          {{ i18n(translations.getStarted) }}
        </p>

        <RevInputSelect
          id="integrators-select"
          v-model="selectedIntegrator"
          :error="integratorError"
          :label="i18n(translations.selectIntegrator)"
          :options="integrators"
        />
      </template>

      <template v-if="currentStep === FormStep.AccessToken">
        <p class="heading-3 mb-12">
          {{
            i18n(translations.collectTokenTitle, {
              integrator: selectedIntegratorName,
            })
          }}
        </p>

        <RevCard class="mb-32 p-16">
          <p class="body-1-bold mb-4 break-words">
            {{ token }}
          </p>

          <CopyTokenToClipboard :token />
        </RevCard>

        <RevInfoBlock
          class="mb-24"
          :content="i18n(translations.tokenInfoBlockContent)"
          :icon="IconLightbulb"
          :title="i18n(translations.tokenInfoBlockTitle)"
          variant="default"
        />
      </template>
    </template>

    <template #footer>
      <template v-if="currentStep === FormStep.ChooseIntegrator">
        <RevButton
          :disabled="!selectedIntegrator"
          full-width="always"
          :loading="isTransitioning"
          variant="primary"
          @click="handleActionChooseIntegratorClick"
        >
          {{ i18n(translations.validate) }}
        </RevButton>
      </template>

      <template v-if="currentStep === FormStep.AccessToken">
        <RevButton
          full-width="always"
          :loading="isTransitioning"
          variant="primary"
          @click="handleActionAccessTokenClick"
        >
          {{ i18n(translations.done) }}
        </RevButton>
      </template>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { HttpApiError, merchantsAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDrawer } from '@ds/components/Drawer'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputSelect } from '@ds/components/InputSelect'
import { closeModal } from '@ds/components/ModalBase'
import { IconLightbulb } from '@ds/icons/IconLightbulb'

import { MODAL_NAMES } from '../../Integration.constants'

import translations from './CreateIntegrationForm.translations'
import { FormStep } from './CreateIntegrationForm.types'
import CopyTokenToClipboard from './components/CopyTokenToClipboard/CopyTokenToClipboard.vue'

const emit = defineEmits(['created'])

const i18n = useI18n()

const { data: integrators } = useHttpFetch(
  merchantsAPI.getIntegrationsMetadata,
  {
    transform: (data) =>
      data.availableIntegrators.map((integrator) => ({
        label: integrator.name,
        value: integrator.id.toString(),
      })),
    default: () => [] as { label: string; value: string }[],
  },
)

const selectedIntegrator = ref('')
const integratorError = ref('')
const currentStep = ref<FormStep>(FormStep.ChooseIntegrator)
const token = ref('')
const isTransitioning = ref(false)

const selectedIntegratorName = computed(
  () =>
    integrators.value.find(({ value }) => value === selectedIntegrator.value)
      ?.label || '',
)

function reset() {
  currentStep.value = FormStep.ChooseIntegrator
  selectedIntegrator.value = ''
}

function handleClose() {
  reset()
}

async function handleActionChooseIntegratorClick() {
  isTransitioning.value = true
  integratorError.value = ''

  try {
    const response = await $httpFetch(merchantsAPI.postIntegration, {
      body: {
        integratorId: selectedIntegrator.value,
      },
    })

    token.value = response.token
    emit('created')
    currentStep.value = FormStep.AccessToken
  } catch (error) {
    const { title } = HttpApiError.fromAnyError(error as Error)
    integratorError.value = title
  } finally {
    isTransitioning.value = false
  }
}

function handleActionAccessTokenClick() {
  reset()
  closeModal(MODAL_NAMES.CREATE)
}
</script>
